// @ts-nocheck
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, X, ArrowRight, Database, Layers, Check, GitCompare, AlertTriangle, Send } from 'lucide-react';
import axios from 'axios';
import { supabase } from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import { SessionExpiredError } from '../../utils/sessionExpiredError';
import './ComparativeFileUpload.css';

const ComparativeFileUpload = ({ onFileUpload, onPeriodDiscovery, onProcessStart, onError, loading }) => {
  const [file, setFile] = useState(null);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  
  // Comparative mode state
  const [selectedRawSheet, setSelectedRawSheet] = useState('');
  const [selectedComparables, setSelectedComparables] = useState([]);
  const [quotaError, setQuotaError] = useState(null);
  const [quotaRequestStatus, setQuotaRequestStatus] = useState('idle'); // idle | sending | sent | error

  const handleRequestQuota = async () => {
    if (quotaRequestStatus === 'sent' || quotaRequestStatus === 'sending') return;
    setQuotaRequestStatus('sending');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userEmail = session?.user?.email || 'Unknown';
      const { error } = await supabase.functions.invoke('request-quota', {
        body: {
          userEmail,
          tenantName: quotaError.tenantName,
          usage: quotaError.usage,
          quota: quotaError.quota,
        },
      });
      if (error) throw error;
      setQuotaRequestStatus('sent');
    } catch (err) {
      logger.error('Failed to send quota request:', err);
      setQuotaRequestStatus('error');
    }
  };

  // Fetch available sheet names from the uploaded file
  const fetchSheetNames = async (uploadedFile) => {
    setLoadingSheets(true);
    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      await supabase.auth.getSession();
      
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/get-sheets/`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );
      const sheets = response.data.sheets || [];
      setAvailableSheets(sheets);
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('❌ Error fetching sheets:', error);
      onError('Failed to read Excel sheets. Please check the file.');
      setAvailableSheets([]);
    } finally {
      setLoadingSheets(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      const ext = uploadedFile.name.toLowerCase();
      if (ext.endsWith('.xlsx') || ext.endsWith('.xlsm') || ext.endsWith('.csv')) {
        setFile(uploadedFile);
        // Reset selections
        setSelectedRawSheet('');
        setSelectedComparables([]);
        // Fetch sheet names when file is uploaded
        fetchSheetNames(uploadedFile);
      } else {
        onError('Please upload an Excel file (.xlsx, .xlsm) or CSV file (.csv)');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: loading
  });

  const removeFile = () => {
    setFile(null);
    setAvailableSheets([]);
    setSelectedRawSheet('');
    setSelectedComparables([]);
  };

  const toggleComparable = (sheet) => {
    if (loading) return;
    setSelectedComparables(prev => {
      if (prev.includes(sheet)) {
        return prev.filter(s => s !== sheet);
      }
      if (prev.length >= 5) {
        onError('Maximum 5 comparable sheets allowed');
        return prev;
      }
      return [...prev, sheet];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      onError('Please select a file first');
      return;
    }

    // Comparative mode validation
    if (!selectedRawSheet) {
      onError('Please select one Raw Data Sheet');
      return;
    }
    if (selectedComparables.length < 2) {
      onError('Please select at least 2 Comparable P&L Sheets');
      return;
    }

    try {
      onProcessStart();

      // 0. Pre-flight quota check (lightweight, no LLM calls)
      try {
        await axios.get(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/check-quota/`,
          {
            withCredentials: true,
            headers: { 'x-api-key': import.meta.env.VITE_API_KEY },
            timeout: 10000,
          }
        );
      } catch (quotaErr) {
        const detail = quotaErr.response?.data?.detail;
        if (detail?.code === 'QUOTA_EXCEEDED') {
          setQuotaError({ usage: detail.usage, quota: detail.quota, tenantName: detail.tenant_name });
          setQuotaRequestStatus('idle');
          onError(null);
          return;
        }
        throw quotaErr;
      }

      const headers = {
        'Content-Type': 'multipart/form-data',
        'x-api-key': import.meta.env.VITE_API_KEY,
      };

      // 1. Discover Raw Data (parallel with comparables)
      const rawFormData = new FormData();
      rawFormData.append('file', file);
      rawFormData.append('sheet_name', selectedRawSheet);
      
      const rawPromise = axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/discover-comparative-data/`,
        rawFormData,
        { headers, withCredentials: true, timeout: 120000 }
      );

      // 2. Discover each Comparable (parallel)
      const compPromises = selectedComparables.map(sheet => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sheet_name', sheet);
        return axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/discover-periods/`,
          formData,
          { headers, withCredentials: true, timeout: 120000 }
        ).then(res => ({ sheet, data: res.data }));
      });

      // 3. Wait for all discoveries
      const [rawResult, ...compResults] = await Promise.all([rawPromise, ...compPromises]);

      // 4. Align periods (include raw data periods)
      const alignmentPayload = {
        comparable_periods: compResults.reduce((acc, { sheet, data }) => {
          acc[sheet] = data;
          return acc;
        }, {}),
        raw_data_periods: rawResult.data.unique_dates || []
      };
      
      const alignmentResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/align-comparable-periods/`,
        alignmentPayload,
        { 
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': import.meta.env.VITE_API_KEY,
          },
          timeout: 120000 
        }
      );

      // 5. Pass to parent (NO config selection for comparative - uses fixed categories)
      const comparativeData = {
        rawData: rawResult.data,
        rawSheetName: selectedRawSheet,
        comparables: compResults,
        alignment: alignmentResponse.data,
        file: file
      };
      
      onPeriodDiscovery(comparativeData, 'comparative');
      onFileUpload(file, 'Multiple Sheets', 'comparative_income_statement', 'comparative');
      
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('❌ Error:', error);
      const detail = error.response?.data?.detail;
      if (detail?.code === 'QUOTA_EXCEEDED') {
        setQuotaError({ usage: detail.usage, quota: detail.quota, tenantName: detail.tenant_name });
        setQuotaRequestStatus('idle');
        // Reset parent loading state so the spinner stops
        onError(null);
      } else {
        onError(typeof detail === 'string' ? detail : error.message);
      }
    }
  };

  const canSubmit = file && selectedRawSheet && selectedComparables.length >= 2 && !quotaError;

  return (
    <div className={`comparative-upload-container border-beam-container ${loading ? 'loading' : ''}`}>
      <div className="border-beam"></div>
      <div className="upload-header">
        <div className="header-icon">
          <GitCompare size={24} />
        </div>
        <div>
          <h3>Comparative Analysis</h3>
          <p className="upload-description">Upload a workbook with ERP data and comparable P&L sheets</p>
        </div>
      </div>
      
      {quotaError && (
        <div className="quota-banner">
          <div className="quota-banner-header">
            <div className="quota-banner-title">
              <AlertTriangle size={18} />
              <span>Quota Reached</span>
            </div>
            <button className="quota-banner-close" onClick={() => setQuotaError(null)}>
              <X size={14} />
            </button>
          </div>
          <p className="quota-banner-message">
            Your organization has used <strong>{quotaError.usage}</strong> of <strong>{quotaError.quota}</strong> allocated entities.
          </p>
          <button
            className="quota-request-btn btn-coral-animated"
            onClick={handleRequestQuota}
            disabled={quotaRequestStatus === 'sending' || quotaRequestStatus === 'sent'}
          >
            {quotaRequestStatus === 'sending' && (
              <>
                <div className="button-spinner"></div>
                Sending Request...
              </>
            )}
            {quotaRequestStatus === 'sent' && (
              <>
                <Check size={14} />
                Request Sent
              </>
            )}
            {quotaRequestStatus === 'error' && (
              <>
                <Send size={14} />
                Retry Request
              </>
            )}
            {quotaRequestStatus === 'idle' && (
              <>
                <Send size={14} />
                Request More Quota
              </>
            )}
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="upload-form">
        {/* File Drop Zone */}
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${file ? 'has-file' : ''} ${loading ? 'disabled' : ''}`}
        >
          <input {...getInputProps()} />
          {file ? (
            <div className="file-info">
              <FileSpreadsheet size={48} className="file-icon" />
              <div className="file-details">
                <p className="file-name">{file.name}</p>
                <p className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button type="button" onClick={removeFile} className="remove-file-btn" disabled={loading}>
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="dropzone-content">
              <Upload size={48} className="upload-icon" />
              <p className="dropzone-text">
                {isDragActive ? "Drop file here..." : "Drag & drop Excel file"}
              </p>
            </div>
          )}
        </div>

        {/* Loading State for Sheets */}
        {file && loadingSheets && (
          <div className="sheets-loading-container">
            <div className="sheets-loading-spinner">
              <div className="button-spinner"></div>
            </div>
            <div className="sheets-loading-text">
              <span className="loading-title">Analyzing workbook...</span>
              <span className="loading-subtitle">Discovering available sheets</span>
            </div>
          </div>
        )}

        {/* Two-Column Sheet Selector */}
        {!loadingSheets && availableSheets.length > 0 && (
          <div className="sheet-selection-container">
            <div className="sheet-column">
              <div className="sheet-header">
                <Database size={16} />
                <div className="sheet-header-text">
                  <h4 className="sheet-header-title">1. Raw Data Sheet</h4>
                  <p className="sheet-header-desc">Select ERP export (Entity/Account/Date/Value)</p>
                </div>
              </div>
              <div className="sheet-list">
                {availableSheets.map((sheet) => (
                  <div
                    key={`raw-${sheet}`}
                    className={`sheet-item ${selectedRawSheet === sheet ? 'selected-raw' : ''} ${loading ? 'disabled' : ''}`}
                    onClick={() => {
                      if (!loading) {
                        setSelectedRawSheet(sheet);
                        // Remove from comparables if it was selected there
                        setSelectedComparables(prev => prev.filter(s => s !== sheet));
                      }
                    }}
                  >
                    <div className="radio-circle">
                      {selectedRawSheet === sheet && <div className="radio-dot" />}
                    </div>
                    <span className="sheet-item-name">{sheet}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="sheet-column">
              <div className="sheet-header">
                <Layers size={16} />
                <div className="sheet-header-text">
                  <h4 className="sheet-header-title">2. Comparable P&L Sheets</h4>
                  <p className="sheet-header-desc">Select 2-5 sheets to compare ({selectedComparables.length}/5)</p>
                </div>
              </div>
              <div className="sheet-list">
                {availableSheets
                  .filter(sheet => sheet !== selectedRawSheet)
                  .map((sheet) => (
                    <div
                      key={`comp-${sheet}`}
                      className={`sheet-item ${selectedComparables.includes(sheet) ? 'selected-comp' : ''} ${loading ? 'disabled' : ''}`}
                      onClick={() => toggleComparable(sheet)}
                    >
                      <div className={`checkbox-square ${selectedComparables.includes(sheet) ? 'checked' : ''}`}>
                        {selectedComparables.includes(sheet) && <Check size={12} />}
                      </div>
                      <span className="sheet-item-name">{sheet}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="analyze-button btn-animated"
          disabled={loading || !canSubmit}
        >
          {loading ? (
            <>
              <div className="button-spinner"></div>
              Discovering...
            </>
          ) : (
            <>
              Discover & Align Periods
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ComparativeFileUpload;

